import React, { useEffect, useState } from 'react';
import { ArrowLeft, BookOpen, Library, Coins, GraduationCap, Download, Crown, Sparkles, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODE_STYLES = {
  tutoring: { border: 'border-purple-400/40', glow: 'shadow-purple-500/20', accent: 'text-purple-300', btn: 'bg-purple-500 hover:bg-purple-400', Icon: GraduationCap },
  artifact: { border: 'border-amber-400/40', glow: 'shadow-amber-500/20', accent: 'text-amber-300', btn: 'bg-amber-400 hover:bg-amber-300', Icon: Download },
  membership: { border: 'border-emerald-400/40', glow: 'shadow-emerald-500/20', accent: 'text-emerald-300', btn: 'bg-emerald-400 hover:bg-emerald-300', Icon: Crown },
};

function OfferingCard({ offering }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const s = MODE_STYLES[offering.mode] || MODE_STYLES.artifact;
  const Icon = s.Icon;

  const spend = async () => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/api/exchange/spend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, offering_id: offering.id, email: email || null }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.detail || 'Something went wrong.');
      } else {
        setResult(data);
      }
    } catch {
      setError('Network error — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid={`exchange-offering-${offering.id}`}
      className={`rounded-2xl border-2 ${s.border} bg-gradient-to-b from-gray-900 via-black to-black p-6 flex flex-col shadow-lg ${s.glow}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border ${s.border}`}>
          <Icon className={`w-5 h-5 ${s.accent}`} />
        </span>
        <span className={`text-sm font-extrabold ${s.accent} inline-flex items-center gap-1.5`}>
          <Coins className="w-4 h-4" />
          {offering.tokens} token{offering.tokens > 1 ? 's' : ''}
        </span>
      </div>
      <h3 className="text-lg font-bold text-white leading-tight mb-2">{offering.title}</h3>
      <p className="text-xs text-gray-400 leading-relaxed flex-1">{offering.description}</p>

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid={`exchange-unlock-${offering.id}`}
          className={`mt-5 w-full ${s.btn} text-black font-bold py-2.5 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2`}
        >
          <Sparkles className="w-4 h-4" /> Unlock with tokens
        </button>
      ) : (
        <div className="mt-5 space-y-2" data-testid={`exchange-spend-panel-${offering.id}`}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Bearer code · SCHX-XXXX-XXXX"
            data-testid={`exchange-code-input-${offering.id}`}
            className="w-full bg-black/60 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400/60 font-mono"
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional, for receipt)"
            data-testid={`exchange-email-input-${offering.id}`}
            className="w-full bg-black/60 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400/60"
          />
          <button
            onClick={spend}
            disabled={busy || code.trim().length < 4}
            data-testid={`exchange-spend-btn-${offering.id}`}
            className={`w-full ${s.btn} disabled:opacity-40 text-black font-bold py-2.5 rounded-full text-sm transition-colors`}
          >
            {busy ? 'Verifying…' : `Spend ${offering.tokens} token${offering.tokens > 1 ? 's' : ''}`}
          </button>
          {error && (
            <p className="text-xs text-red-400" data-testid={`exchange-error-${offering.id}`}>{error}</p>
          )}
          {result && (
            <div
              className="rounded-lg border border-emerald-400/40 bg-emerald-950/30 p-3 text-xs text-emerald-100 space-y-2"
              data-testid={`exchange-result-${offering.id}`}
            >
              <p>{result.message}</p>
              {result.download_url && (
                <a
                  href={`${API_URL}${result.download_url}`}
                  data-testid={`exchange-download-${offering.id}`}
                  className="inline-flex items-center gap-1.5 font-bold text-emerald-300 hover:text-emerald-200 underline"
                >
                  <Download className="w-3.5 h-3.5" /> Download artifact
                </a>
              )}
              {result.session_pass && (
                <p className="font-mono text-emerald-300">Pass ID: {result.session_pass}</p>
              )}
              {typeof result.tokens_remaining === 'number' && (
                <p className="text-emerald-300/80">{result.tokens_remaining} tokens left on this code.</p>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function ExchangePage() {
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/exchange/offerings`);
        const data = await r.json();
        setOfferings(Array.isArray(data.offerings) ? data.offerings : []);
      } catch {
        setOfferings([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0b0a14] text-stone-200" data-testid="exchange-page">
      <div className="bg-gradient-to-r from-amber-900/30 via-purple-900/30 to-emerald-900/30 border-b border-amber-300/25">
        <div className="container py-4 flex items-center justify-between">
          <a
            href="/university"
            data-testid="exchange-back-home"
            className="text-xs font-mono uppercase tracking-[0.22em] text-amber-200 hover:text-amber-100 inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> HumanCodex University
          </a>
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/70">
            The Exchange · Tri-Modal Commerce
          </span>
        </div>
      </div>

      <main className="container max-w-6xl px-6 py-14">
        <header className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight" data-testid="exchange-title">
            The <span className="text-amber-300">Exchange</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-gray-300">
            Three modes of commerce, one currency. Tokens are currency; everything costs tokens.
            One token = $5, minted through Gumroad, spendable anywhere on this node.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <a
              href="/university#catalog"
              data-testid="exchange-redeem-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm transition-colors"
            >
              <Coins className="w-4 h-4" /> Spend tokens on a course
            </a>
            <a
              href="https://nextxus.one"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="exchange-buy-tokens-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 text-xs font-mono uppercase tracking-[0.18em] transition-colors"
            >
              Buy tokens at Storefront <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </header>

        {/* Commerce Tiers — Architect directive: $5 Token · $25 Council · Scholar Direct */}
        <div className="grid md:grid-cols-3 gap-4 mb-14" data-testid="exchange-commerce-tiers">
          <a
            href="https://nextxus.one"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tier-token"
            className="rounded-2xl border-2 border-amber-400/50 bg-amber-950/20 p-5 hover:border-amber-300/80 transition-colors"
          >
            <Coins className="w-6 h-6 text-amber-300 mb-2" />
            <h2 className="text-base md:text-lg font-bold text-white">Tier 1 · $5 Access Token</h2>
            <p className="text-xs text-gray-400 mt-1">The bearer instrument. Buy at the Storefront, spend anywhere on this node. →</p>
          </a>
          <a
            href="https://next-xus.com/council"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tier-council"
            className="rounded-2xl border-2 border-purple-400/50 bg-purple-950/20 p-5 hover:border-purple-300/80 transition-colors"
          >
            <Crown className="w-6 h-6 text-purple-300 mb-2" />
            <h2 className="text-base md:text-lg font-bold text-white">Tier 2 · $25 Council</h2>
            <p className="text-xs text-gray-400 mt-1">Council membership — join the Federation's governing circle. →</p>
          </a>
          <div
            data-testid="tier-scholar-direct"
            className="rounded-2xl border-2 border-dashed border-cyan-400/40 bg-cyan-950/10 p-5 opacity-80"
          >
            <GraduationCap className="w-6 h-6 text-cyan-300 mb-2" />
            <h2 className="text-base md:text-lg font-bold text-white">Tier 3 · Scholar Direct</h2>
            <p className="text-xs text-gray-400 mt-1">Direct consultation with Xavier — <span className="text-cyan-300 font-bold">Coming Soon</span>.</p>
          </div>
        </div>

        {/* Mode anchors — the tri-modal frame */}
        <div className="grid md:grid-cols-3 gap-4 mb-14" data-testid="exchange-mode-anchors">
          <a
            href="/university#catalog"
            data-testid="exchange-mode-courses"
            className="rounded-2xl border border-purple-400/30 bg-purple-950/20 p-5 hover:border-purple-300/60 transition-colors"
          >
            <BookOpen className="w-6 h-6 text-purple-300 mb-2" />
            <h2 className="text-base md:text-lg font-bold text-white">Mode I · Courses</h2>
            <p className="text-xs text-gray-400 mt-1">45+ University courses. Token-gated or free. →</p>
          </a>
          <a
            href="/library"
            data-testid="exchange-mode-library"
            className="rounded-2xl border border-cyan-400/30 bg-cyan-950/20 p-5 hover:border-cyan-300/60 transition-colors"
          >
            <Library className="w-6 h-6 text-cyan-300 mb-2" />
            <h2 className="text-base md:text-lg font-bold text-white">Mode II · Books &amp; Library</h2>
            <p className="text-xs text-gray-400 mt-1">The Federation document archive and inscribed canon. →</p>
          </a>
          <div
            data-testid="exchange-mode-exchange"
            className="rounded-2xl border-2 border-amber-400/50 bg-amber-950/20 p-5"
          >
            <Sparkles className="w-6 h-6 text-amber-300 mb-2" />
            <h2 className="text-base md:text-lg font-bold text-white">Mode III · The Exchange</h2>
            <p className="text-xs text-gray-400 mt-1">Tutoring sessions · Digital artifacts · Memberships. You are here.</p>
          </div>
        </div>

        {/* Third-mode offerings */}
        {loading ? (
          <div className="text-center py-16" data-testid="exchange-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-400 mx-auto"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="exchange-offerings-grid">
            {offerings.map((o) => (
              <OfferingCard key={o.id} offering={o} />
            ))}
          </div>
        )}

        <p className="mt-10 text-center text-[10px] font-mono uppercase tracking-[0.22em] text-gray-500">
          Scholar Circle members unlock all artifacts at no extra tokens · Bearer-instrument logic · DIR-073
        </p>
      </main>
    </div>
  );
}
